import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Terminal, Plus, Trash2, Edit, Tag, Webhook, BarChart3
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function WhatsAppCommands() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCommand, setEditingCommand] = useState(null);
  const [formData, setFormData] = useState({
    trigger: '',
    response: '',
    addTags: '',
    removeTags: '',
    webhookUrl: '',
    scope: 'all',
    isActive: true
  });

  // Get business ID
  const getBusinessId = useCallback(async () => {
    if (user?.business_id) return user.business_id;
    if (user?.role === 'admin') {
      const res = await api.get('/businesses');
      if (res.data && res.data.length > 0) {
        return res.data[0].id;
      }
    }
    return null;
  }, [user]);

  const fetchCommands = useCallback(async () => {
    try {
      setLoading(true);
      const businessId = await getBusinessId();
      if (!businessId) return;

      const response = await api.get(`/whatsapp/commands?businessId=${businessId}`);
      const data = response.data;
      setCommands(data.commands || []);
    } catch (error) {
      console.error('Error fetching commands:', error);
      toast.error(t('cmds.toastLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [getBusinessId]);

  useEffect(() => {
    fetchCommands();
  }, [fetchCommands]);

  const handleOpenCreate = () => {
    setEditingCommand(null);
    setFormData({
      trigger: '',
      response: '',
      addTags: '',
      removeTags: '',
      webhookUrl: '',
      scope: 'all',
      isActive: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (command) => {
    setEditingCommand(command);
    setFormData({
      trigger: command.trigger,
      response: command.response,
      addTags: command.config?.addTags?.join(', ') || '',
      removeTags: command.config?.removeTags?.join(', ') || '',
      webhookUrl: command.config?.webhookUrl || '',
      scope: command.config?.scope || 'all',
      isActive: command.config?.isActive !== false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.trigger || !formData.response) {
      toast.error(t('cmds.toastRequired'));
      return;
    }

    try {
      const businessId = await getBusinessId();
      if (!businessId) {
        toast.error(t('cmds.toastNoBusiness'));
        return;
      }

      const payload = {
        businessId,
        trigger: formData.trigger,
        response: formData.response,
        config: {
          addTags: formData.addTags ? formData.addTags.split(',').map(x => x.trim()).filter(Boolean) : [],
          removeTags: formData.removeTags ? formData.removeTags.split(',').map(x => x.trim()).filter(Boolean) : [],
          webhookUrl: formData.webhookUrl || null,
          scope: formData.scope,
          isActive: formData.isActive
        }
      };

      let data;
      if (editingCommand) {
        const response = await api.put(`/whatsapp/commands/${editingCommand.commandId}`, payload);
        data = response.data;
      } else {
        const response = await api.post('/whatsapp/commands', payload);
        data = response.data;
      }

      if (data.success) {
        toast.success(editingCommand ? t('cmds.toastUpdated') : t('cmds.toastCreated'));
        setShowModal(false);
        fetchCommands();
      } else {
        toast.error(data.error || t('cmds.toastSaveFailed'));
      }
    } catch (error) {
      console.error('Error saving command:', error);
      toast.error(t('cmds.toastSaveFailed'));
    }
  };

  const handleDelete = async (command) => {
    if (!window.confirm(t('cmds.confirmDelete', { trigger: command.trigger }))) return;

    try {
      const response = await api.delete(`/whatsapp/commands/${command.commandId}`);
      const data = response.data;

      if (data.success) {
        toast.success(t('cmds.toastDeleted'));
        fetchCommands();
      } else {
        toast.error(data.error || t('cmds.toastDeleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting command:', error);
      toast.error(t('cmds.toastDeleteFailed'));
    }
  };

  const getScopeBadge = (scope) => {
    const config = {
      all: { label: t('cmds.scopeAll'), className: 'bg-gray-100 text-gray-700' },
      groups: { label: t('cmds.scopeGroups'), className: 'bg-purple-100 text-purple-700' },
      individual: { label: t('cmds.scopeIndividual'), className: 'bg-blue-100 text-blue-700' }
    };
    const c = config[scope] || config.all;
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="whatsapp-commands-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('cmds.title')}</h1>
          <p className="text-muted-foreground">{t('cmds.subtitle')}</p>
        </div>
        <Button onClick={handleOpenCreate} data-testid="add-command-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('cmds.newCommand')}
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Terminal className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">{t('cmds.bannerTitle')}</p>
              <p className="text-sm text-blue-700">
                {t('cmds.bannerText')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commands List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('cmds.listTitle')}</CardTitle>
          <CardDescription>
            {t('cmds.listSub')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('cmds.loading')}
            </div>
          ) : commands.length === 0 ? (
            <div className="text-center py-12">
              <Terminal className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t('cmds.emptyTitle')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('cmds.emptySub')}
              </p>
              <Button onClick={handleOpenCreate}>
                <Plus className="w-4 h-4 mr-2" />
                {t('cmds.createCommand')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {commands.map((command) => (
                <div 
                  key={command.commandId}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                        /{command.trigger}
                      </code>
                      {getScopeBadge(command.config?.scope)}
                      {!command.config?.isActive && (
                        <Badge variant="outline" className="bg-red-50 text-red-700">{t('cmds.inactive')}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {command.response}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {command.config?.addTags?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          +{command.config.addTags.join(', ')}
                        </span>
                      )}
                      {command.config?.webhookUrl && (
                        <span className="flex items-center gap-1">
                          <Webhook className="w-3 h-3" />
                          Webhook
                        </span>
                      )}
                      {command.stats?.timesUsed > 0 && (
                        <span className="flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" />
                          {command.stats.timesUsed} {t('cmds.uses')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(command)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(command)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCommand ? t('cmds.editTitle') : t('cmds.newTitle')}</DialogTitle>
            <DialogDescription>
              {t('cmds.modalSub')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="trigger">{t('cmds.triggerLabel')}</Label>
              <Input
                id="trigger"
                placeholder={t('cmds.triggerPh')}
                value={formData.trigger}
                onChange={(e) => setFormData({ ...formData, trigger: e.target.value.toLowerCase().replace(/\s/g, '') })}
              />
              <p className="text-xs text-muted-foreground">
                {t('cmds.triggerHint', { cmd: formData.trigger || t('cmds.triggerFallback') })}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="response">{t('cmds.responseLabel')}</Label>
              <Textarea
                id="response"
                placeholder={t('cmds.responsePh')}
                rows={4}
                value={formData.response}
                onChange={(e) => setFormData({ ...formData, response: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="addTags">{t('cmds.addTagsLabel')}</Label>
                <Input
                  id="addTags"
                  placeholder="tag1, tag2, tag3"
                  value={formData.addTags}
                  onChange={(e) => setFormData({ ...formData, addTags: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">{t('cmds.tagsHint')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="removeTags">{t('cmds.removeTagsLabel')}</Label>
                <Input
                  id="removeTags"
                  placeholder="tag1, tag2"
                  value={formData.removeTags}
                  onChange={(e) => setFormData({ ...formData, removeTags: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhookUrl">{t('cmds.webhookLabel')}</Label>
              <Input
                id="webhookUrl"
                placeholder="https://tu-servidor.com/webhook"
                value={formData.webhookUrl}
                onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                {t('cmds.webhookHint')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('cmds.scopeLabel')}</Label>
                <Select 
                  value={formData.scope} 
                  onValueChange={(value) => setFormData({ ...formData, scope: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('cmds.scopeAll')}</SelectItem>
                    <SelectItem value="groups">{t('cmds.scopeGroups')}</SelectItem>
                    <SelectItem value="individual">{t('cmds.scopeIndividual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between pt-6">
                <Label>{t('cmds.activeLabel')}</Label>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              {t('cmds.cancel')}
            </Button>
            <Button onClick={handleSave}>
              {editingCommand ? t('cmds.saveChanges') : t('cmds.createCommand')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
